/**
 * One `request()` for the whole REST surface.
 *
 * Two things live here and nowhere else:
 *
 * 1. **The `Authorization` header.** Nothing else in the app reads the token.
 * 2. **P5's error envelope.** Every non-2xx becomes an `ApiError` with `code`, `message`
 * and `requestId`, and is thrown. Parsing three different error shapes at
 * every call site and surfaced the result through `alert()`; there is exactly one parser
 * here, and callers `catch (e) { if (e instanceof ApiError) … }`.
 *
 * The turn endpoint is deliberately absent — `POST /stories/{id}/turns` is an SSE stream
 * with its own non-idempotent, non-retriable semantics and it belongs to `src/turn/`
 * (D4). It shares `authHeader()` and `ApiError` and nothing else.
 */
import type { ActiveTurn, ErrorCode, ErrorDetail, ErrorEnvelope, paths } from '@/api/generated';

/**
 * Codes minted on this side of the wire. They are never in P5's closed set.
 *
 * `stream_interrupted` is the turn machine's: the turn stream ended without its terminal event. It is
 * deliberately *not* `network`, because the two mean opposite things to a player — a turn
 * whose connection dropped is still running, still costs $0.13, and still commits.
 */
export type ClientErrorCode = 'network' | 'malformed_response' | 'stream_interrupted';

export type ApiErrorCode = ErrorCode | ClientErrorCode;

export class ApiError extends Error {
  /** `code` is the thing to branch on. `message` is the fallback and the developer's hint. */
  readonly code: ApiErrorCode;
  /** 0 for `network` — the request never got a response. */
  readonly status: number;
  /** The CloudWatch correlation id, for the "report this" affordance that pre-fills feedback. */
  readonly requestId: string | null;
  /** Non-null only on the 409 that P3's turn conflict produces. */
  readonly activeTurn: ActiveTurn | null;

  constructor(
    code: ApiErrorCode,
    message: string,
    options: { status?: number; requestId?: string | null; activeTurn?: ActiveTurn | null } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = options.status ?? 0;
    this.requestId = options.requestId ?? null;
    this.activeTurn = options.activeTurn ?? null;
  }
}

/**
 * Whether retrying could plausibly help. Used by the Query client's `retry` (queries.ts):
 * a 404 or a `story_limit_reached` retried three times is three identical failures and a
 * slower error message.
 */
export function isRetriable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.code === 'network') return true;
  return error.status >= 500;
}

// --- the base URL ----------------------------------------------------------------------

/**
 * Unset `VITE_API_BASE_URL` means "run against mocks" (D9): requests stay same-origin and
 * MSW answers them. There is no default host and no fallback — a typo'd env var must fail
 * loudly against the dev origin, not quietly against production.
 */
export function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

// --- auth wiring -----------------------------------------------------------------------

let tokenProvider: () => string | null = () => null;
const unauthenticatedHandlers = new Set<() => void>();

/**
 * The auth layer injects the token getter here at mount. Dependency direction matters: the
 * client knows nothing about Google, and `auth/` knows nothing about routes or queries.
 */
export function setAuthTokenProvider(provider: () => string | null): void {
  tokenProvider = provider;
}

/**
 * Fires on every 401, from anywhere (D8: "a 401 mid-session drops to a signed-out state").
 * Returns the unsubscribe, so a remounting provider cannot leak a stale handler.
 */
export function onUnauthenticated(handler: () => void): () => void {
  unauthenticatedHandlers.add(handler);
  return () => {
    unauthenticatedHandlers.delete(handler);
  };
}

/** Exposed for `src/turn/`, which owns its own fetch but shares the 401 transition. */
export function notifyUnauthenticated(): void {
  for (const handler of unauthenticatedHandlers) handler();
}

/** Exposed for `src/turn/`, which must send the same header on the streaming POST. */
export function authHeader(): Record<string, string> {
  const token = tokenProvider();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Test seam. Production code never calls this. */
export function resetApiClient(): void {
  tokenProvider = () => null;
  unauthenticatedHandlers.clear();
}

// --- typed paths -----------------------------------------------------------------------
//
// The point of generating types (D5) is that a wrong path, a missing path parameter or a
// misspelled query key is a red build rather than a runtime `undefined`. These helpers are
// what carry that from `generated.ts` into the call sites.

export type HttpMethod = 'get' | 'post' | 'patch' | 'delete';

/** Paths that actually declare this method — `paths['/me']['delete']` is `never`. */
export type PathsWith<M extends HttpMethod> = {
  [P in keyof paths]: paths[P] extends Record<M, unknown> ? P : never;
}[keyof paths];

type Operation<M extends HttpMethod, P extends PathsWith<M>> = paths[P][M];

type PathParams<Op> = Op extends { parameters: { path?: infer T } } ? Exclude<T, undefined> : never;
type QueryParams<Op> = Op extends { parameters: { query?: infer T } }
  ? Exclude<T, undefined>
  : never;
type JsonBody<Op> = Op extends { requestBody?: { content: { 'application/json': infer B } } }
  ? Exclude<B, undefined>
  : never;

type JsonOf<R> = R extends { content: { 'application/json': infer J } } ? J : never;
/** 200, else 201, else the response has no body (a 204) and the call resolves to `void`. */
type OkBody<Op> = Op extends { responses: infer R }
  ? 200 extends keyof R
    ? JsonOf<R[200]>
    : 201 extends keyof R
      ? JsonOf<R[201]>
      : void
  : void;

type PathOption<Op> = [PathParams<Op>] extends [never]
  ? { path?: undefined }
  : { path: PathParams<Op> };

type QueryOption<Op> = [QueryParams<Op>] extends [never]
  ? { query?: undefined }
  : Record<string, never> extends QueryParams<Op>
    ? { query?: QueryParams<Op> }
    : { query: QueryParams<Op> };

type BodyOption<Op> = [JsonBody<Op>] extends [never]
  ? { body?: undefined }
  : Op extends { requestBody: unknown }
    ? { body: JsonBody<Op> }
    : { body?: JsonBody<Op> };

export type RequestOptions<Op> = PathOption<Op> &
  QueryOption<Op> &
  BodyOption<Op> & {
    signal?: AbortSignal;
    headers?: Record<string, string>;
  };

// --- the request ------------------------------------------------------------------------

export async function request<M extends HttpMethod, P extends PathsWith<M>>(
  method: M,
  path: P,
  ...[options]: Record<string, never> extends RequestOptions<Operation<M, P>>
    ? [options?: RequestOptions<Operation<M, P>>]
    : [options: RequestOptions<Operation<M, P>>]
): Promise<OkBody<Operation<M, P>>> {
  const opts = (options ?? {}) as {
    path?: Record<string, string | number>;
    query?: Record<string, string | number | boolean | undefined>;
    body?: unknown;
    signal?: AbortSignal;
    headers?: Record<string, string>;
  };

  const headers = new Headers({ Accept: 'application/json', ...authHeader(), ...opts.headers });
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(opts.body);
  }

  // Built outside the try: a programming error here must surface as itself, not get
  // laundered into a `network` failure that blames the player's wifi.
  const url = buildUrl(path, opts.path, opts.query);

  let response: Response;
  try {
    response = await fetch(url, {
      method: method.toUpperCase(),
      headers,
      body,
      signal: opts.signal,
    });
  } catch (cause) {
    // An abort is the caller's own doing — TanStack Query and the turn machine both need to
    // see the DOMException, not a failure wearing its clothes.
    if (opts.signal?.aborted) throw cause;
    throw new ApiError('network', 'The network request could not be completed.', {
      status: 0,
    });
  }

  if (!response.ok) throw await apiErrorFromResponse(response);

  return (await readBody(response)) as OkBody<Operation<M, P>>;
}

function buildUrl(
  template: string,
  pathParams: Record<string, string | number> | undefined,
  query: Record<string, string | number | boolean | undefined> | undefined,
): string {
  const path = template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = pathParams?.[name];
    if (value === undefined) {
      // Only reachable if the generated types and this call disagree, which is the whole
      // failure mode `PathOption` exists to prevent — so make it loud rather than fetching
      // a URL with a literal `{story_id}` in it.
      throw new Error(`Missing path parameter "${name}" for ${template}`);
    }
    return encodeURIComponent(String(value));
  });

  const url = new URL(`${apiBaseUrl()}${path}`, globalThis.location?.origin ?? 'http://localhost');
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.headers.get('Content-Length') === '0') return undefined;
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError('malformed_response', 'The API returned a body that is not JSON.', {
      status: response.status,
      requestId: response.headers.get('X-Request-Id'),
    });
  }
}

/**
 * The one non-2xx parser. Exported because `src/turn/` owns its own `fetch` (D4) and must
 * read a rejected turn — a 409 with `active_turn`, a 503 mid-deploy — the same way.
 */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  if (response.status === 401) notifyUnauthenticated();

  const headerRequestId = response.headers.get('X-Request-Id');
  let payload: unknown;
  try {
    payload = JSON.parse(await response.text()) as unknown;
  } catch {
    payload = undefined;
  }

  const envelope = readEnvelope(payload);
  if (!envelope) {
    // Not our API talking: an ALB 502, a CloudFront error page, a proxy timeout. The status
    // is the only trustworthy thing in it.
    return new ApiError(
      response.status === 401 ? 'unauthenticated' : 'internal',
      `${response.status} ${response.statusText}`.trim(),
      { status: response.status, requestId: headerRequestId },
    );
  }

  return new ApiError(envelope.error.code, envelope.error.message, {
    status: response.status,
    requestId: envelope.error.request_id || headerRequestId,
    activeTurn: readActiveTurn(payload),
  });
}

/**
 * The SSE `error` event carries the same `{code, message, request_id}` object as every
 * non-2xx body (the world codex: "one client-side mapper, not two"). This is that mapper's other door.
 *
 * `status` stays 0: the HTTP response was a 200 that then failed mid-stream, and reporting
 * 200 for a failure would make `isRetriable` and `isReportable` both wrong.
 */
export function apiErrorFromDetail(detail: ErrorDetail): ApiError {
  return new ApiError(detail.code, detail.message, { requestId: detail.request_id });
}

function readEnvelope(payload: unknown): ErrorEnvelope | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const error = (payload as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return null;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (typeof code !== 'string' || typeof message !== 'string') return null;
  return payload as ErrorEnvelope;
}

function readActiveTurn(payload: unknown): ActiveTurn | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const activeTurn = (payload as { active_turn?: unknown }).active_turn;
  if (typeof activeTurn !== 'object' || activeTurn === null) return null;
  const { chapter_number, started_at } = activeTurn as {
    chapter_number?: unknown;
    started_at?: unknown;
  };
  if (typeof chapter_number !== 'number' || typeof started_at !== 'string') return null;
  return { chapter_number, started_at };
}
